import {
    trackCancelRestoreClick,
    trackRestoreClick,
    trackRestoreFailedLoad,
    trackRestoreFromCRMClick,
    trackRestoreSuccessLoad,
    trackSubscriptionRestoreClick,
} from 'analytics/trackers/restoreSubscription';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isUserFromCRM } from 'store/auth/selectors';
import { closeModal, openModal } from 'store/modals/actions';
import { ModalName } from 'store/modals/types';
import { restoreSubscription } from 'store/subscriptions/actions';
import { selectIsOneYearMainSubscription, selectIsReturnOfferApplied } from 'store/subscriptions/selectors';

import { CURRENCY_SIGN_MAP } from 'constants/currency';
import { PRODUCT_CODES, PRODUCT_TYPES } from 'constants/product';
import { PATHS } from 'constants/routerPaths';

import { useDispatch, useSelector } from 'hooks';

import { Product } from 'types/product';
import { ISubscription } from 'types/subscription';

const useRestoreSubscription = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isFromCRM: boolean = useSelector(isUserFromCRM);
    const isOneYearSubscription: boolean = useSelector(selectIsOneYearMainSubscription);
    const withAlreadyAppiledOffer: boolean = useSelector(selectIsReturnOfferApplied);

    const productRef = useRef<ISubscription | null>(null);

    const handleCloseModal = () => {
        dispatch(closeModal());
    };

    const initRestoreSubscription = (candidate: ISubscription) => {
        if (isFromCRM && !isOneYearSubscription && !withAlreadyAppiledOffer) {
            trackRestoreFromCRMClick();
            navigate(PATHS.RETURN_OFFER);

            return;
        }

        const { product, product_code: productCode, subscription_period } = candidate;

        productRef.current = candidate;

        const isVIPSupport = productCode === PRODUCT_CODES.APP_VIP_SUPPORT;
        const subscriptionType = isVIPSupport ? PRODUCT_TYPES.VIP_SUPPORT : PRODUCT_TYPES.PERSONALIZED_PROGRAM;
        const { currency, price } = product;

        trackRestoreClick(subscriptionType, candidate.external_id);

        dispatch(
            openModal(ModalName.restoreSubscriptionModal, {
                onClose: () => {
                    trackCancelRestoreClick();
                    handleCloseModal();
                },
                onDiscard: () => {
                    trackCancelRestoreClick();
                    handleCloseModal();
                },
                onSubmit: () => {
                    trackSubscriptionRestoreClick(subscriptionType, candidate.external_id);
                    restoreSubmit(candidate);
                },
                captions: {
                    title: t('subscription.restore.modal.title'),
                    description: t('subscription.restore.modal.description', {
                        currencySign: CURRENCY_SIGN_MAP[currency],
                        price,
                        period: subscription_period,
                    }),
                    submitButton: t('subscription.restore.modal.accept.buttonText'),
                    discardButton: t('basics.cancel'),
                },
            })
        );
    };

    const handleSuccessfulRestore = () => {
        const { currency, price } = productRef.current?.product as Product;

        dispatch(
            openModal(ModalName.restoreSubscriptionModal, {
                onClose: () => {
                    handleCloseModal();
                },
                onSubmit: () => {
                    handleCloseModal();
                },
                trackLoad: trackRestoreSuccessLoad,
                captions: {
                    title: t('subscription.planRestored.modal.title'),
                    description: t('subscription.restore.modal.description', {
                        currencySign: CURRENCY_SIGN_MAP[currency],
                        price,
                        period: productRef.current?.subscription_period,
                    }),
                    submitButton: t('basics.continue'),
                },
            })
        );
    };

    const restoreSubmit = (candidate: ISubscription) => {
        dispatch(
            openModal(ModalName.waitingModal, {
                captions: {
                    title: t('subscription.updatingPlan.inProgress'),
                },
            })
        );
        dispatch(
            restoreSubscription({
                id: candidate.external_id,
                provider: candidate.payment_provider,
                onSuccess: () => {
                    handleSuccessfulRestore();
                },
                onError: () => {
                    trackRestoreFailedLoad();
                    handleCloseModal();
                },
            })
        );
    };

    return {
        initRestoreSubscription,
    };
};

export default useRestoreSubscription;
